// ---------------------------------------------------------------------------
// V24 — THE CLIENT PORTAL. S1 to S6.
//
// 18-client-portal.md §1: "it is a distribution channel, not a feature… which
// means it has to be better than the console, not a subset of it." And the trap
// it names, B7: "if V24 degrades into a nicer export, we have built Liveforce's
// product."
//
// ---------------------------------------------------------------------------
// THE ONE RULE THAT SHAPES EVERY SCREEN BELOW
//
// §4's S1: "Each blocker names the requirement and shows WHO OWNS THE FIX, which
// is the agency. The client sees the problem and the owner. THE CLIENT NEVER
// SEES A BUTTON THAT RESOLVES IT. That is the boundary the whole portal rests
// on."
//
// So there is exactly one screen here with a control that writes — S4 — and
// everything it writes is a decision ABOUT the agency's work, never a change TO
// it. A change request creates a task and says so in its own confirmation,
// because a client who believes they have changed the roster will not chase it
// and will be surprised on the day.
//
// ---------------------------------------------------------------------------
// WHY S1 IS SPLIT INTO A CONTAINER AND A PURE VIEW, WHICH NOTHING ELSE HERE IS
//
// §8 checks 8 and 11 are assertions about what RENDERS: the scope-reduction
// annotation must appear in zone 3, and a severed poll must show a stale marker
// and a timestamp rather than the last-known number alone. Both are things a
// unit test over a helper cannot see and a grep over a component cannot prove.
// `ReadinessView` takes its payload as a prop and runs no effect, so
// scripts/lib/portal-render.mjs can render it to static markup and read the
// result — which is a real render of the real component, not a paraphrase.
//
// HANDOFF.md §5 is right that a render check cannot prove a screen is good: it
// passes on a page of dashes. It can prove a specific sentence reaches a
// specific zone, and that is exactly what those two checks ask.
//
// ---------------------------------------------------------------------------
// NOTHING HERE SENDS A NOTIFICATION
//
// §2.2's ruling: "V24 writes notification intents to a table and does not send
// them. V25 drains the table. This is stated so the V24 builder does not
// improvise an email path that V25 then has to unpick." There is no mail, no
// toast subscription and no preference control on this surface — S7 Account is
// deferred to V25 with the same reasoning, recorded in MORNING-2026-08-15.md.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { send } from "../api/auth";
import {
  portal,
  type ClientDecision,
  type PortalApprovals,
  type PortalAvailability,
  type PortalChange,
  type PortalDocuments,
  type PortalReadiness,
  type PortalRoster,
  type PortalSeatState,
  type PortalWorker,
  type ProgrammeEvent,
} from "../api/portal";
import { AddButton } from "../design/AddLink";
import { Notice } from "../design/Notice";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import { toneOf } from "../design/tone";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel, translateRefusal } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { typedTimeHint } from "../system/clock";
import { span, whenCompact, whenIn, whenRange } from "../system/format";
import {
  coverageSentence,
  freshness,
  pollIntervalMs,
  seatTone,
  secondsClock,
  trendBars,
} from "../system/portal";

// ---------------------------------------------------------------------------
// Shared plumbing
// ---------------------------------------------------------------------------

// `failure` keeps the read's status for design/LoadFailed; `failed` stays the words, for the screens that
// show a refusal beside data already read.
type Load<T> = { data: T | null; failed: string | null; failure: Failure | null; reload: () => void };

type ChangeRequestRow = { id: string; body: string; status_label: string; status_tone: string; created_at: string };

function useRead<T>(spec: () => Parameters<typeof send>[0], pick: (body: never) => T | undefined, deps: unknown[]) {
  const { locale, t, refusal } = useT();
  const [state, setState] = useState<Omit<Load<T>, "reload">>({ data: null, failed: null, failure: null });
  const [reloads, setReloads] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const build = useCallback(spec, deps);
  useEffect(() => {
    let live = true;
    void (async () => {
      const res = await send<never>(build());
      if (!live) return;
      const value = res.status === 200 ? pick(res.body) : undefined;
      if (value !== undefined) setState({ data: value, failed: null, failure: null });
      else {
        const body = res.body as { error?: { message?: string } } | null;
        setState((s) => ({
          data: s.data,
          failed: refusal(body?.error) ?? t("That could not be read."),
          failure: { status: res.status, refusal: refusal(body?.error) },
        }));
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [build, reloads]);
  return { ...state, reload: () => setReloads((n) => n + 1) };
}

/**
 * The event's own sub-navigation.
 *
 * FIVE DESTINATIONS AND NO SIXTH. S7 Account is not here because it is not
 * built; V4's rule about the main navigation applies just as hard on a surface
 * a client sees, and a link to a screen that does not exist is a way to reach a
 * 404 that looks exactly like a bug to the person who taps it.
 */
function EventTabs({ id, here }: { id: string; here: string }) {
  const { locale, t } = useT();
  const tabs = [
    { key: "", label: t("Readiness") },
    { key: "roster", label: t("Roster") },
    { key: "changes", label: t("Changes") },
    { key: "approvals", label: t("Approvals") },
    { key: "documents", label: t("Documents") },
  ];
  return (
    <nav className="tabs" aria-label={t("This event")}>
      {tabs.map((tab) => (
        <Link
          key={tab.key || "readiness"}
          to={`/portal/events/${encodeURIComponent(id)}${tab.key ? `/${tab.key}` : ""}`}
          aria-current={tab.key === here ? "page" : undefined}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  );
}

// ===========================================================================
// S1 — EVENT READINESS. THE HERO.
// ===========================================================================

/**
 * The pure half. No fetch, no interval, no clock of its own.
 *
 * `now` and `lastOkAt` ARRIVE AS PROPS and are not read from Date.now() inside,
 * which is what makes the stale marker testable at all: a component that asked
 * the wall clock what time it was could only be checked by waiting.
 */
export function ReadinessView({
  data,
  lastOkAt,
  now,
  wall,
}: {
  data: PortalReadiness;
  lastOkAt: number | null;
  now: number;
  wall: boolean;
}) {
  const { t, locale } = useT();
  const inWindow = false;
  const fresh = freshness({ lastOkAt, now, intervalMs: pollIntervalMs(inWindow) });
  const bars = trendBars(data.trend);
  const annotated = bars.filter((b) => b.annotation !== null);

  return (
    <div className={wall ? "stack wall" : "stack"}>
      {/* ---- ZONE 1. THE NUMBER, and it must be legible from six metres. ---- */}
      <section className="panel">
        <div className="portal-hero">
          <span className="portal-score numeric">{data.score === null ? "—" : Math.round(data.score)}</span>
          <span className="portal-state">
            <StatusChip tone={toneOf(data.state.tone)} label={translateLabel(locale, data.state.label)} />
          </span>
        </div>
        <p className="portal-coverage numeric">
          {coverageSentence(data.coverage, data.critical_posts.dark, locale)}
        </p>

        {/*
          §7.3 — IT DEGRADES HONESTLY. The marker and the timestamp travel
          together and the timestamp is always shown, fresh or stale: a reader
          who only ever sees a clock when something is wrong has no idea what a
          working clock looks like, so the absence of one tells them nothing.
        */}
        <p>
          <StatusChip tone={fresh.tone} label={translateLabel(locale, fresh.label)} />{" "}
          <span className="portal-owner numeric">
            {t("last updated {time}", { time: secondsClock(data.computed_at) })}
          </span>
        </p>
        {fresh.stale ? (
          <p className="portal-owner">
            {t("This screen has stopped receiving updates. The number above is the last one that arrived and is not current.")}
          </p>
        ) : null}
      </section>

      {/* ---- ZONE 2. BLOCKERS, IN CLIENT LANGUAGE, WITH THEIR OWNER. ---- */}
      <section className="panel">
        <div className="panel-head">
          <h2>{t("What is in the way")}</h2>
        </div>
        {data.blockers.length === 0 ? (
          <p className="muted">{t("Nothing is outstanding on this event.")}</p>
        ) : (
          <ul className="portal-blockers">
            {data.blockers.map((b) => (
              <li key={`${b.code}-${b.sentence}`}>
                <div>{translateRefusal(locale, { message: b.sentence, sentence_key: b.sentence_key, params: b.params })}</div>
                <div className="portal-owner">{t("Owned by {owner}", { owner: translateLabel(locale, b.owner) })}</div>
              </li>
            ))}
          </ul>
        )}
        {data.critical_posts.dark > 0 ? (
          <p className="portal-owner">
            {t("Critical posts with nobody on them: {places}", { places: data.critical_posts.dark_places.join(" · ") })}
          </p>
        ) : null}
      </section>

      {/* ---- ZONE 3. TREND, AND THE SCOPE-REDUCTION ANNOTATIONS. ---- */}
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Readiness over time")}</h2>
        </div>
        {bars.length === 0 ? (
          <p className="muted">{t("No readiness has been recorded for this event yet.")}</p>
        ) : (
          <div className="portal-trend">
            {bars.map((bar) => (
              <div key={bar.at}>
                {/* A BAR AND A NUMBER. §7.5: a projector is a colour-degrading
                    device, so a fill alone says nothing.

                    THE NUMBER IS OUTSIDE THE COLUMN AND THAT IS LOAD-BEARING —
                    see portal.css. A bar sharing a flex context with its own
                    label shrinks to match its siblings, and five different
                    scores rendered as five identical bars. */}
                <div className="portal-trend-col">
                  <div
                    className="portal-trend-bar"
                    style={{ "--bar-height": `${bar.heightPercent}%` } as React.CSSProperties}
                  />
                </div>
                <div className="portal-owner numeric">{bar.scoreLabel}</div>
              </div>
            ))}
          </div>
        )}

        {/*
          §V17.4's ANTI-GAMING RULE, ON THE SURFACE THAT RULE NAMES.
          18-client-portal.md §4: "That anti-gaming rule was written naming this
          surface. It is not optional and it is not softenable. An agency that
          quietly deletes the posts it cannot fill is exactly the behaviour the
          annotation exists to make visible, and the portal is where it has to be
          visible."
        */}
        {annotated.map((bar) => (
          <p key={`annotation-${bar.at}`} className="portal-annotation">
            <span className="numeric">{secondsClock(bar.at)}</span> — {bar.annotation}
          </p>
        ))}
      </section>
    </div>
  );
}

/**
 * The container. Polls, and it is the poll that §7.1 asks for.
 *
 * ONE INTERVAL DOES BOTH JOBS. It fires the request AND advances `now`, so the
 * stale marker appears on schedule even though nothing has answered — which is
 * the case it exists for. A screen that only re-rendered on a successful
 * response could never show that it had stopped receiving them.
 */
function ReadinessScreen({ wall }: { wall: boolean }) {
  const { id = "" } = useParams();
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [data, setData] = useState<PortalReadiness | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const inWindow = useRef(false);

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      const res = await send<PortalReadiness & { error?: { message: string } }>(
        portal.readiness(apiBase, id),
      );
      if (!live) return;
      setNow(Date.now());
      // THE BODY IS THE PAYLOAD, NOT A WRAPPER AROUND ONE. `ok()` in the Worker
      // returns what it is given, so /v1/portal/events/:id/readiness answers
      // { event, score, state, … } at the top level and there is no `readiness`
      // key to reach through. An earlier draft read one, and the gallery could
      // not see the mistake because its fixture had been written to match the
      // draft — the demo agreeing with itself while both disagreed with the API.
      if (res.status === 200 && res.body?.state) {
        setData(res.body);
        setLastOkAt(Date.now());
        setFailed(null);
      } else {
        setFailed(refusal(res.body?.error) ?? t("This event could not be read."));
      }
      if (live) timer = setTimeout(() => void tick(), pollIntervalMs(inWindow.current));
    };

    void tick();
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, id]);

  if (data === null) {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Event readiness")}</h2>
        </div>
        {failed === null ? (
          <p className="muted">{t("Reading this event…")}</p>
        ) : (
          <StatusChip tone="failed" label={failed} />
        )}
      </section>
    );
  }

  return (
    <>
      {wall ? null : (
        <>
          <h1>{data.event.name}</h1>
          <p className="muted">
            {data.event.venue_name ?? t("No venue recorded")} · {whenIn(data.event.starts_at, orgZone)} ·{" "}
            {span(data.event.starts_at, data.event.ends_at)}
          </p>
          <EventTabs id={id} here="" />
          <p>
            <Link className="btn btn-secondary" to={`/portal/events/${encodeURIComponent(id)}/wall`}>
              {t("Show on a wall")}
            </Link>
          </p>
        </>
      )}
      {failed === null ? null : <StatusChip tone="attention" label={failed} />}
      <ReadinessView data={data} lastOkAt={lastOkAt} now={now} wall={wall} />
    </>
  );
}

export function PortalReadinessScreen() {
  return <ReadinessScreen wall={false} />;
}

/**
 * §7.2's wall. THE SAME COMPONENT WITH THE CHROME DROPPED — "one CSS state, not
 * a second application." A wall that computed its own numbers would be a second
 * answer to the same question, and the first time the two disagreed nobody would
 * trust either.
 */
export function PortalWallScreen() {
  return <ReadinessScreen wall={true} />;
}

// ===========================================================================
// S6 — PROGRAMME. The portal's front page.
// ===========================================================================
export function PortalProgramme() {
  const { apiBase, me } = useSession();
  const { locale, t } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const { data, failed, failure, reload } = useRead<ProgrammeEvent[]>(
    () => portal.events(apiBase),
    (body: never) => (body as { events?: ProgrammeEvent[] })?.events,
    [apiBase],
  );

  // Quiet list: a column that says nothing for every row is not shown.
  const anyCovered = (data ?? []).some((e) => e.roster !== null);
  const anyWaiting = (data ?? []).some((e) => e.open_change_requests > 0);

  return (
    <div className="stack">
      <h1>{t("Your events")}</h1>
      <section className="panel">
        {failure !== null && data === null ? <LoadFailed failure={failure} fallback={t("That could not be read.")} onRetry={reload} /> : null}
        {data === null && failed === null ? <p className="muted">{t("Reading your events…")}</p> : null}
        {data !== null && data.length === 0 ? (
          <p className="muted">
            {t("No events have been shared with you yet. They appear here as soon as your agency creates them — before anybody is booked, not after.")}
          </p>
        ) : null}
        {data !== null && data.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("Event")}</th>
                <th>{t("When")}</th>
                <th>{t("Venue")}</th>
                <th>{t("Roster")}</th>
                {anyCovered ? <th>{t("Covered")}</th> : null}
                {anyWaiting ? <th>{t("Waiting on you")}</th> : null}
              </tr>
            </thead>
            <tbody>
              {data.map((e) => (
                <tr key={e.event_id}>
                  <td>
                    <Link to={`/portal/events/${encodeURIComponent(e.event_id)}`}>{e.name}</Link>
                    {e.operational_window_open ? (
                      <div>
                        <StatusChip tone="live" label={t("Running now")} />
                      </div>
                    ) : null}
                  </td>
                  <td className="numeric nowrap">{(() => {
                      const w = whenRange(e.starts_at, e.ends_at, orgZone);
                      return (
                        <>
                          {w.main}
                          {w.yours ? <span className="muted"><br />({w.yours})</span> : null}
                        </>
                      );
                    })()}</td>
                  <td>{e.venue_name ?? "—"}</td>
                  <td>
                    {/* §9 question 2: an event with no published roster is SHOWN,
                        saying so. Hiding it until it is filled is the opacity we
                        are selling against. */}
                    {e.roster === null ? (
                      <StatusChip tone="neutral" label={t("Not published yet")} />
                    ) : (
                      <span className="numeric">
                        {t("Version {n}", { n: e.roster.version_no })} · {whenCompact(e.roster.published_at ?? "", orgZone)}
                      </span>
                    )}
                  </td>
                  {anyCovered ? (
                    <td className="numeric">
                      {e.roster === null ? "—" : t("{covered} of {seats}", { covered: e.roster.covered_count, seats: e.roster.seat_count })}
                    </td>
                  ) : null}
                  {anyWaiting ? <td className="numeric">{e.open_change_requests || "—"}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>
    </div>
  );
}

// ===========================================================================
// S2 — ROSTER
// ===========================================================================
export function PortalRosterScreen() {
  const { id = "" } = useParams();
  const { apiBase, me } = useSession();
  const { locale, t } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [zone, setZone] = useState("");

  const { data, failed, failure, reload } = useRead<PortalRoster>(
    () => portal.roster(apiBase, id),
    (body: never) => ((body as PortalRoster)?.posts === undefined ? undefined : (body as PortalRoster)),
    [apiBase, id],
  );
  const vocab = useRead<PortalSeatState[]>(
    () => portal.seatStates(apiBase),
    (body: never) => (body as { portal_seat_states?: PortalSeatState[] })?.portal_seat_states,
    [apiBase],
  );

  const derivation = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of vocab.data ?? []) map[s.id] = s.derived_from;
    return map;
  }, [vocab.data]);

  const zones = useMemo(() => {
    const seen = new Set<string>();
    for (const p of data?.posts ?? []) if (p.zone) seen.add(p.zone);
    return [...seen].sort();
  }, [data]);

  const posts = (data?.posts ?? []).filter((p) => zone === "" || p.zone === zone);

  return (
    <div className="stack">
      <h1>{t("Roster")}</h1>
      <EventTabs id={id} here="roster" />

      <section className="panel">
        {/*
          §4's S2's last rule: "The client ALWAYS knows which of the two things
          they are looking at." The published version and the live clock are
          printed together, from one response, so they cannot be out of step.
        */}
        <p className="numeric">
          {data?.roster_version
            ? t("Roster version {n}, published {when}", { n: data.roster_version.version_no, when: whenIn(data.roster_version.published_at, orgZone) })
            : t("No roster has been published for this event yet.")}
          {data?.live_as_at ? ` · ${t("Live check-in as at {time}", { time: secondsClock(data.live_as_at) })}` : ""}
        </p>
        {failure !== null && data === null ? <LoadFailed failure={failure} fallback={t("That could not be read.")} onRetry={reload} /> : null}
        {vocab.failed !== null ? (
          <Notice tone="attention">{t("The state vocabulary could not be read, so each state is shown by its word alone.")}</Notice>
        ) : null}

        {/* An export is a SERVER call, not a browser download of a list already
            fetched: §8 check 9 requires every export to write a
            client_portal_view row, and a client-side CSV would leave no trace. */}
        {posts.length > 0 || zone !== "" ? (
        <p>
          <label htmlFor="portal-zone">{t("Zone")}</label>{" "}
          <select id="portal-zone" value={zone} onChange={(e) => setZone(e.target.value)}>
            <option value="">{t("All zones")}</option>
            {zones.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>{" "}
          <a className="btn btn-secondary" href={`${apiBase}/v1/portal/events/${encodeURIComponent(id)}/roster?export=csv`}>
            {t("Export CSV")}
          </a>{" "}
          <a className="btn btn-secondary" href={`${apiBase}/v1/portal/events/${encodeURIComponent(id)}/roster?export=xlsx`}>
            {t("Export XLSX")}
          </a>
        </p>
        ) : null}
      </section>

      <section className="panel">
        {data === null && failed === null ? <p className="muted">{t("Reading the roster…")}</p> : null}
        {data !== null && posts.length === 0 ? (
          <p className="muted">
            {zone === "" ? t("No posts on this roster.") : t("No posts on this roster in {zone}.", { zone })}
          </p>
        ) : null}
        {posts.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("Post")}</th>
                <th>{t("Zone")}</th>
                <th>{t("When")}</th>
                <th>{t("State")}</th>
                <th>{t("Who")}</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.seq}>
                  <td>
                    {p.post_name}
                    {p.is_supervisor ? <span className="portal-owner"> · {t("supervising post")}</span> : null}
                    {p.is_reserve ? <span className="portal-owner"> · {t("on standby")}</span> : null}
                  </td>
                  <td>{p.zone ?? "—"}</td>
                  <td className="numeric nowrap">{(() => {
                      const w = whenRange(p.starts_at, p.ends_at, orgZone);
                      return (
                        <>
                          {w.main}
                          {w.yours ? <span className="muted"><br />({w.yours})</span> : null}
                        </>
                      );
                    })()}</td>
                  <td>
                    <StatusChip tone={seatTone(derivation[p.state.code] ?? "")} label={p.state.label} />
                  </td>
                  {/* §6.4: a display name and a role. No phone, no email, no
                      photo, no reliability figure, and no expiry date against a
                      name — none of which this response even carries. */}
                  <td>{p.worker_name ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>
    </div>
  );
}

// ===========================================================================
// S3 — CHANGES
// ===========================================================================
export function PortalChangesScreen() {
  const { id = "" } = useParams();
  const { apiBase, me } = useSession();
  const { t, locale } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const { data, failed, failure, reload } = useRead<PortalChange[]>(
    () => portal.changes(apiBase, id),
    (body: never) => (body as { changes?: PortalChange[] })?.changes,
    [apiBase, id],
  );

  return (
    <div className="stack">
      <h1>{t("What changed")}</h1>
      <EventTabs id={id} here="changes" />
      <section className="panel">
        <p className="muted">
          {t("Everything that has moved since you last opened this screen. Opening it moves the marker forward, so the next visit answers the same question about a later day.")}
        </p>
        {failure !== null && data === null ? <LoadFailed failure={failure} fallback={t("That could not be read.")} onRetry={reload} /> : null}
        {data === null && failed === null ? <p className="muted">{t("Reading the changes…")}</p> : null}
        {data !== null && data.length === 0 ? (
          <p className="muted">{t("Nothing has changed since you last looked.")}</p>
        ) : null}
        {data !== null && data.length > 0 ? (
          <ul className="portal-blockers">
            {data.map((c, i) => (
              <li key={`${c.change}-${c.post_name}-${i}`}>
                <div>{translateRefusal(locale, { message: c.sentence, sentence_key: c.sentence_key, params: c.params })}</div>
                <div className="portal-owner numeric">
                  {c.zone ? `${c.zone} · ` : ""}
                  {whenIn(c.starts_at, orgZone)} · {span(c.starts_at, c.ends_at)}
                </div>
                {/* §4's S3: scope reductions are MARKED, and carry the same
                    annotation S1 zone 3 does. */}
                {c.is_scope_reduction ? (
                  <StatusChip tone="attention" label={t("Scope reduced — posts removed from the plan")} />
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}

// ===========================================================================
// S4 — APPROVALS. The only screen where a client writes.
// ===========================================================================
export function PortalApprovalsScreen() {
  const { id = "" } = useParams();
  const { apiBase, me } = useSession();
  const { t, refusal, locale } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [data, setData] = useState<PortalApprovals | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [composing, setComposing] = useState(false);
  const [busy, setBusy] = useState(false);

  /**
   * WHAT A CLIENT MAY SAY, READ FROM THE SERVER AND NEVER SPELLED HERE.
   *
   * The first version of this screen wrote the words into the buttons, and one of
   * them — the obvious one — had ALREADY been renamed in the schema, so the button
   * would have posted a decision the foreign key does not accept. See api/portal.ts.
   *
   * A vocabulary that does not arrive leaves the row with NO decide buttons rather
   * than a default, which is the same refusal every other screen in this product
   * makes: absolute rule 4 says a select is never invented, and a decision is a
   * stronger case than a select because it is written to an append-only table.
   */
  const decisions = useRead<ClientDecision[]>(
    () => portal.decisions(apiBase),
    (body: never) => (body as { client_decisions?: ClientDecision[] })?.client_decisions,
    [apiBase],
  );
  const decisionLabel = (id: string | null) =>
    id === null ? null : (decisions.data ?? []).find((d) => d.id === id)?.label ?? id;

  const load = useCallback(async () => {
    const res = await send<PortalApprovals & { error?: { message: string } }>(
      portal.approvals(apiBase, id),
    );
    if (res.status === 200 && res.body?.items) {
      setData(res.body);
      setFailed(null);
    } else {
      setFailed(refusal(res.body?.error) ?? t("This queue could not be read."));
    }
  }, [apiBase, id, t, refusal]);

  // SWEEP S14. WHAT THIS CLIENT ASKED FOR, AND THE ANSWER. The portal could raise a
  // request and never see it again; the agency's answer is now shown where it was asked.
  // S69 (0202): the state arrives worded for the client, with its tone.
  const [requests, setRequests] = useState<ChangeRequestRow[]>([]);
  const loadRequests = useCallback(async () => {
    try {
      const res = await send<{ change_requests?: ChangeRequestRow[] }>(
        portal.myChangeRequests(apiBase, id),
      );
      setRequests(res.status === 200 ? (res.body.change_requests ?? []) : []);
    } catch {
      setRequests([]);
    }
  }, [apiBase, id]);
  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = useCallback(
    async (subjectType: string, subjectId: string, decision: string) => {
      setBusy(true);
      const res = await send<{ error?: { message: string } }>(
        portal.decide(apiBase, {
          event_id: id,
          subject_type: subjectType,
          subject_id: subjectId,
          decision,
          ...(note.trim() === "" ? {} : { note: note.trim() }),
        }),
      );
      setBusy(false);
      if (res.status >= 400) {
        setFailed(refusal(res.body?.error) ?? t("That decision could not be recorded."));
        return;
      }
      setNote("");
      setSaid(t("Recorded. Your agency can see it, and it is in the event's evidence record."));
      await load();
    },
    [apiBase, id, note, load, t, refusal],
  );

  const raise = useCallback(async () => {
    if (note.trim() === "") return;
    setBusy(true);
    const res = await send<{ notice?: string; error?: { message: string } }>(
      portal.requestChange(apiBase, { event_id: id, body: note.trim() }),
    );
    setBusy(false);
    if (res.status >= 400) {
      setFailed(refusal(res.body?.error) ?? t("That request could not be raised."));
      return;
    }
    setNote("");
    setComposing(false);
    setSaid(res.body?.notice ? translateLabel(locale, res.body.notice) : t("Your agency has been asked. Nothing on the roster has changed yet."));
    await loadRequests();
  }, [apiBase, id, note, loadRequests, t, refusal, locale]);

  return (
    <div className="stack">
      <h1>{t("Approvals")}</h1>
      <EventTabs id={id} here="approvals" />

      <section className="panel">
        {/* §4's S4 rule 2, said on the screen and not only in the response: a
            portal that can stop a gate opening will be removed from the
            contract, and a client who BELIEVES it can is as bad. */}
        <p className="muted">{data?.notice ? translateLabel(locale, data.notice) : t("Your decision is recorded, not required.")}</p>
        {failed !== null ? <StatusChip tone="failed" label={failed} /> : null}
        {said !== null ? <StatusChip tone="live" label={said} /> : null}
        {data !== null && !data.may_decide ? (
          <Notice tone="neutral">{t("You can read this queue. Signing off needs an approver on your account.")}</Notice>
        ) : null}
      </section>

      <section className="panel">
        {data === null && failed === null ? <p className="muted">{t("Reading the queue…")}</p> : null}
        {data !== null && data.items.length === 0 ? (
          <p className="muted">{t("Nothing is waiting for you on this event.")}</p>
        ) : null}
        {data !== null && data.items.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("What")}</th>
                <th>{t("Variance")}</th>
                <th>{t("Your answer")}</th>
                <th>{t("Decide")}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={`${item.subject_type}-${item.subject_id}`}>
                  <td>{item.headline}</td>
                  <td className="numeric">
                    {item.variance_minutes === null ? "—" : t("{minutes} min", { minutes: item.variance_minutes })}
                  </td>
                  <td>
                    {/* §4's S4 rule 4: SILENCE IS RECORDED AS SILENCE. Not
                        pending, not rejected, and never rendered as either. */}
                    {item.decision === null ? (
                      <StatusChip tone="neutral" label={t("You have not said anything")} />
                    ) : (
                      // THE LABEL, NOT THE ID. Every other vocabulary in this product
                      // travels with its word beside it; this one used to render the
                      // raw database id to a client. The id is the fallback, so a
                      // decision the vocabulary has not caught up with still reads as
                      // something rather than as nothing.
                      <StatusChip
                        tone="live"
                        label={`${item.decision_label ?? decisionLabel(item.decision)} · ${whenIn(item.decided_at ?? "", orgZone)}`}
                      />
                    )}
                  </td>
                  <td>
                    {!data.may_decide ? (
                      <span className="portal-owner">{t("Read only")}</span>
                    ) : (decisions.data ?? []).length === 0 ? (
                      // THE DESIGNED REFUSAL. No vocabulary, no buttons, and a
                      // sentence saying why — never an invented default.
                      <span className="portal-owner">
                        {t("The list of decisions could not be read, so nothing can be recorded here yet.")}
                      </span>
                    ) : (
                      /* ONE BUTTON PER DECISION THE SERVER OFFERS, in the order it
                         offers them, with `is_positive` choosing the emphasis. Nothing
                         here knows how many there are or what any of them is called.

                         EVERY DECISION IS OFFERED ON EVERY ROW, and that is a limit
                         rather than a choice: `app.client_approval_subject` records no
                         mapping from a subject to the decisions it permits, so the
                         alternative would be a mapping written here — which is the
                         exact thing this change removed. Recorded for whoever adds
                         that column. */
                      (decisions.data ?? []).map((d) => (
                        <span key={d.id}>
                          <button
                            type="button"
                            className={d.is_positive ? "btn" : "btn btn-secondary"}
                            disabled={busy}
                            title={undefined}
                            onClick={() => void decide(item.subject_type, item.subject_id, d.id)}
                          >
                            {translateLabel(locale, d.label)}
                          </button>{" "}
                        </span>
                      ))
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>

      {data?.may_decide ? (
        <section className="panel">
          <div className="panel-head">
            <h2>{t("Ask for a change")}</h2>
            <AddButton label={t("Ask for a change")} expanded={composing} onClick={() => setComposing(!composing)} />
          </div>
          {composing ? (
            <>
          <p className="muted">
            {t("This creates a task for your agency. It does not change the roster, and nothing on this event moves until they action it.")}
          </p>
          {/*
            A NOTE, AND DELIBERATELY NOT A FREE-TEXT FIELD ABOUT A PERSON.
            Absolute rule 5 and Directive 2024/2831: a box that invites health,
            belief or emotional-state data is a defect. The label asks about the
            WORK, the placeholder shows what a good one looks like, and the
            length is capped by the API at a thousand characters.
          */}
          <p>
            <label htmlFor="portal-note">{t("What needs to change about the work?")}</label>
            <br />
            <textarea
              id="portal-note"
              rows={3}
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("We need two more people on Gate C from 18:00.")}
            />
          </p>
          <p>
            <button type="button" className="btn" disabled={busy || note.trim() === ""} onClick={() => void raise()}>
              {t("Send this to the agency")}
            </button>{" "}
            <button type="button" className="btn btn-quiet" onClick={() => setComposing(false)}>
              {t("Cancel")}
            </button>
          </p>
            </>
          ) : null}
        </section>
      ) : null}

      {requests.length > 0 ? (
        <section className="panel">
          <div className="panel-head">
            <h2>{t("Your requests")}</h2>
          </div>
          <ul className="plain-list">
            {requests.map((r) => (
              <li key={r.id} className="row">
                <StatusChip tone={toneOf(r.status_tone)} label={translateLabel(locale, r.status_label)} />
                <span>{r.body}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

// ===========================================================================
// S5 — DOCUMENTS
// ===========================================================================
export function PortalDocumentsScreen() {
  const { id = "" } = useParams();
  const { apiBase, me } = useSession();
  const { t, locale } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const { data, failed, failure, reload } = useRead<PortalDocuments>(
    () => portal.documents(apiBase, id),
    (body: never) =>
      (body as PortalDocuments)?.credentials === undefined ? undefined : (body as PortalDocuments),
    [apiBase, id],
  );

  return (
    <div className="stack">
      <h1>{t("Documents")}</h1>
      <EventTabs id={id} here="documents" />

      <section className="panel">
        <div className="panel-head">
          <h2>{t("Briefings")}</h2>
        </div>
        {failure !== null && data === null ? <LoadFailed failure={failure} fallback={t("That could not be read.")} onRetry={reload} /> : null}
        {data === null && failed === null ? <p className="muted">{t("Reading the documents…")}</p> : null}
        {data !== null && data.briefings.length === 0 ? (
          <p className="muted">{t("No briefing has been issued for this event.")}</p>
        ) : null}
        {/* §5: an acknowledgement RATE, never names. And the headline already
            carries the count — "confirmed by 143 of 148" — so nothing prints it
            a second time beside it in a second vocabulary. The server owns the
            sentence. */}
        <ul className="plain-list">
          {(data?.briefings ?? []).map((b) => (
            // THE COUNT IS THE POINT (§5: an acknowledgement rate, never names). It
            // arrived beside the headline and was never shown until Run 85.
            <li key={b.headline}>
              {translateLabel(locale, b.headline)}: <span className="numeric">{b.count}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>{t("Credentials")}</h2>
        </div>
        {/* §6.4: a SUMMARY. Never a document, never an expiry date against a name. */}
        {data === null ? null : (
          <p>
            <StatusChip
              tone={data.credentials.exceptions === 0 ? "live" : "attention"}
              label={
                translateRefusal(locale, {
                  message: data.credentials.headline,
                  sentence_key: data.credentials.headline_key,
                  params: data.credentials.params,
                }) ?? data.credentials.headline
              }
            />
          </p>
        )}
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>{t("Risk assessment")}</h2>
        </div>
        {/* 0230 (S107). That one is signed and in force, its version and the day it was
            signed — S5's "where F12 supplies them". The hazards and controls stay with the
            agency; the headline is the server's. */}
        {data !== null && data.risk_assessments.length === 0 ? (
          <p className="muted">{t("No signed risk assessment is on record for this event.")}</p>
        ) : null}
        <ul className="plain-list">
          {(data?.risk_assessments ?? []).map((r, i) => (
            <li key={`${r.headline}-${i}`}>
              <StatusChip tone="live" label={t("Signed off")} />{" "}
              {translateRefusal(locale, { message: r.headline, sentence_key: r.headline_key, params: r.params })} —{" "}
              {t("version {n}", { n: r.version })}
              {r.signed_at ? `, ${t("signed {when}", { when: whenIn(r.signed_at, orgZone) })}` : ""}
            </li>
          ))}
        </ul>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>{t("Proof Packs")}</h2>
        </div>
        {/* §5: "Say so on this screen, in plain language, to a non-technical
            reader. A procurement officer who understands they can verify the
            document independently is a procurement officer who will specify us
            next time." The sentence comes from the server so that the console,
            the pack and this screen cannot describe it three ways. */}
        <p className="muted">{data?.proof_pack_explanation ? translateLabel(locale, data.proof_pack_explanation) : ""}</p>
        {data !== null && data.proof_packs.length === 0 ? (
          <p className="muted">
            {t("No Proof Pack has been issued for this event yet. One is normally issued once the last timesheet has been approved.")}
          </p>
        ) : null}
        <ul className="plain-list">
          {(data?.proof_packs ?? []).map((p) => (
            // No internal id on the screen (event.clinic tour, G5): a client has no use for the pack's UUID here,
            // and the issue date names the pack within its event. Downloading a pack is the owner's open question.
            <li key={p.proof_pack_id ?? ""}>
              {t("Proof Pack issued {when}", { when: whenIn(p.issued_at ?? "", orgZone) })}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

// ===========================================================================
// D12 — IS THIS WORKER FREE?
//
// `docs/DECISION-availability-to-clients-2026-08-24.md`. The owner decided this
// against the recommendation on record; the screen's job is to make the answer
// honest rather than to relitigate the decision.
//
// ★ THREE ANSWERS AND NOT TWO, AND THE SCREEN MUST NOT COLLAPSE THEM. `is_free`
// comes back `true`, `false` or **`null`**, and null means "you may not ask" —
// the worker has not opted into marketplace visibility. Rendering null as "busy"
// would tell a client that somebody who simply declined to be listed is working,
// which is both false and unfixable from either side.
//
// AND `false` IS DELIBERATELY VAGUE IN THE COPY. The API knows it means "booked
// somewhere, possibly by another agency". The sentence here says "not available",
// because a client does not need to be told that a competitor booked them in
// order to plan around it, and the wording is the last place we can decline to
// volunteer it.
// ===========================================================================
export function PortalWorkersScreen() {
  const { apiBase, me } = useSession();
  // Whose clock the availability window below is typed in. See system/clock.ts.
  const { locale, t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [asking, setAsking] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, PortalAvailability>>({});
  // Quiet list: the answer column is a dash for everyone until somebody has been asked.
  const anyAnswered = Object.keys(answers).length > 0;
  const [failed, setFailed] = useState<string | null>(null);

  /**
   * ONE WINDOW FOR THE WHOLE TABLE, not one per row. A client planning a shift is
   * asking the same question about several people, and a per-row date picker
   * makes the comparison — which is the actual task — impossible to see.
   */
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const { data, failed: listFailed, failure: listFailure, reload: listReload } = useRead<PortalWorker[]>(
    () => portal.workers(apiBase),
    (body: never) => (body as { workers?: PortalWorker[] })?.workers,
    [apiBase],
  );

  const windowReady = from !== "" && to !== "" && Date.parse(to) > Date.parse(from);

  const ask = useCallback(
    async (ref: string) => {
      setAsking(ref);
      const res = await send<PortalAvailability & { error?: { message: string } }>(
        portal.availability(apiBase, ref, {
          from: new Date(from).toISOString(),
          to: new Date(to).toISOString(),
        }),
      );
      setAsking(null);
      if (res.status >= 400 || !res.body) {
        setFailed(refusal(res.body?.error) ?? t("That question could not be answered."));
        return;
      }
      setFailed(null);
      setAnswers((prev) => ({ ...prev, [ref]: res.body as PortalAvailability }));
    },
    [apiBase, from, to, t, refusal],
  );

  /** The three answers, as three sentences. Never a colour on its own — §4's S2. */
  const answerChip = (a: PortalAvailability | undefined) => {
    if (a === undefined) return <span className="muted">—</span>;
    if (a.is_free === null) return <StatusChip tone="neutral" label={t("Cannot say")} />;
    return a.is_free
      ? <StatusChip tone="live" label={t("Available")} />
      : <StatusChip tone="neutral" label={t("Not available")} />;
  };

  return (
    <div className="stack">
      <h1>{t("Who is free?")}</h1>

      <section className="panel">
        <p className="muted">
          {t("Pick a window, then ask about anyone who has worked one of your events. We can only answer for people who have chosen to be listed — where we cannot, the row says so rather than guessing.")}
        </p>
        {/* `row` + `field` are the shell's own pair — a wrapping flex row of
            label-above-input columns. An invented `field-row` was the first
            version and it had NO RULE ANYWHERE, so both inputs would have
            rendered unstyled; verify-design-parity caught it before a browser
            did, which is the whole argument for that check existing. */}
        <div className="row">
          <div className="field">
            <label htmlFor="d12-from">{t("From")}</label>
            <input
              id="d12-from"
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="d12-to">{t("To")}</label>
            <input
              id="d12-to"
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>
        {/* SAID PLAINLY RATHER THAN BY DISABLING SILENTLY. A greyed-out button
            with no sentence is the commonest way a form refuses without saying
            what it wants. */}
        {/* ★ WHOSE CLOCK. Both fields are datetime-local and carry no zone, so a
            client in another country asks about a window an hour off the one
            they meant. See system/clock.ts. */}
        <p className="field-hint">{typedTimeHint(orgZone, t)}</p>
        {!windowReady && (from !== "" || to !== "") ? (
          <p className="muted">{t("The end of the window has to be after its start.")}</p>
        ) : null}
      </section>

      <section className="panel">
        {listFailure !== null && data === null ? <LoadFailed failure={listFailure} fallback={t("That could not be read.")} onRetry={listReload} /> : null}
        {failed !== null ? <StatusChip tone="failed" label={failed} /> : null}
        {data === null && listFailed === null ? <p className="muted">{t("Reading your people…")}</p> : null}
        {data !== null && data.length === 0 ? (
          <p className="muted">
            {t("Nobody has worked one of your events yet. People appear here once they have been on a published roster of yours.")}
          </p>
        ) : null}
        {data !== null && data.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("Who")}</th>
                {anyAnswered ? <th>{t("In your window")}</th> : null}
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((w) => (
                <tr key={w.ref}>
                  <td>{w.name}</td>
                  {anyAnswered ? <td>{answerChip(answers[w.ref])}</td> : null}
                  <td>
                    {/* NOT ASKABLE IS EXPLAINED WHERE THE BUTTON WOULD BE, rather
                        than by a button that always answers "cannot say". */}
                    {w.availability_askable ? (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={!windowReady || asking === w.ref}
                        onClick={() => void ask(w.ref)}
                      >
                        {asking === w.ref ? t("Asking…") : t("Ask")}
                      </button>
                    ) : (
                      <span className="muted">{t("Not listed for availability")}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>
    </div>
  );
}
